import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AppConstants } from '@core-lib/config';
import {
	checkMontantFormat,
	compareDatesOnly,
	formatExtensions,
	getDatesToCompare,
	getFileExtension,
	getType,
	isValidDate
} from '@core-lib/functions';
import { PasswordValidators, UniversalValidators } from 'ngx-validators';

type NotEmptyDefaultValueType = 'boolean' | 'number' | 'string' | 'array' | 'custom' | 'object' | 'unknown';
interface NotEmptyError {
	type: NotEmptyDefaultValueType;
	defaultValue?: any;
}
interface ExactLengthError {
	requiredLength: number;
}
interface ExactLengthsConfig {
  alpha?: number[];
  numeric?: number[];
}
interface ExactLengthsError {
	requiredLength: string;
}
interface DaysComparisonError {
	days: number;
}

export class CustomValidators {
	protected static isEmptyInputValue(value: any): boolean {
		// we don't check for string here so it also works with arrays
		return value === undefined || value === null || value.length === 0;
	}

	protected static buildMultipleSirenLengthMessage(config: { alpha?: number[]; numeric?: number[] }): string {
		const formatList = (values: number[], label: string): string => {
			if (values.length === 0) return '';
			if (values.length === 1) return `${values[0]} ${label}`;
			if (values.length === 2) return `${values[0]} ou ${values[1]} ${label}`;

			const allButLast = values.slice(0, -1).join(', ');
			const last = values[values.length - 1];
			return `${allButLast} ou ${last} ${label}`;
		};

		const alphaMsg = config.alpha?.length ? formatList(config.alpha, 'caractères') : '';
		const numericMsg = config.numeric?.length ? formatList(config.numeric, 'chiffres') : '';

		if (alphaMsg && numericMsg) {
			return `soit de ${alphaMsg}, soit de ${numericMsg}`;
		} else {
			return alphaMsg || numericMsg || '';
		}
	}

	public static notNull(control: AbstractControl): ValidationErrors | null {
		return Validators.required(control);
	}

	public static notEmpty(defaultValueForType?: any): ValidatorFn {
		const hasDefaultValue = !!arguments.length;
		return (control: AbstractControl): { noEmptyValue: NotEmptyError } | null => {
			const isDefaultValue = (value: any): NotEmptyDefaultValueType => {
				switch (getType(value)) {
					case 'boolean':
						if (value === false) {
							return 'boolean';
						}
						break;
					case 'number':
						if (value === 0) {
							return 'number';
						}
						break;
					case 'bigint':
						// eslint-disable-next-line eqeqeq
						if (value == 0) {
							return 'number';
						}
						break;
					case 'string':
						if (value.trim().length === 0) {
							return 'string';
						}
						break;
					case 'object':
						if (Array.isArray(value) && value.length === 0) {
							return 'array';
						}

						if (CustomValidators.isEmptyInputValue(value)) {
							return 'object';
						}
						break;
					case 'undefined':
					case 'null':
						return 'unknown';
					case 'function':
					case 'symbol':
					default:
						if (CustomValidators.isEmptyInputValue(value)) {
							return 'object';
						}
						break;
				}

				if (hasDefaultValue && value === defaultValueForType) {
					return 'custom';
				}
			};

			const defaultValueType = isDefaultValue(control.value);
			if (defaultValueType) {
				const error: NotEmptyError = {
					type: defaultValueType
				};

				if (defaultValueType === 'custom') {
					error.defaultValue = defaultValueForType;
				}

				return { noEmptyValue: error };
			}

			return null;
		};
	}

	public static number(control: AbstractControl): ValidationErrors | null {
		return UniversalValidators.isNumber(control);
	}

	public static integer(control: AbstractControl): ValidationErrors | null {
		return !('' + control.value).match(/[\., ]/) && Number.isInteger(+control.value) ? null : { numberRequired: true };
	}

	public static numberAllowComma(control?: AbstractControl): ValidatorFn {
		return Validators.pattern(/^[0-9]{1,10}([,.][0-9]{1,10})?$/);
	}

	public static atLeastDigitChar(Montant: number): ValidatorFn {
		return PasswordValidators.digitCharacterRule(Montant);
	}

	public static atLeastLowercaseChar(Montant: number): ValidatorFn {
		return PasswordValidators.lowercaseCharacterRule(Montant);
	}

	public static atLeastUppercaseChar(Montant: number): ValidatorFn {
		return PasswordValidators.uppercaseCharacterRule(Montant);
	}

	public static atLeastSpecialChar(Montant: number): ValidatorFn {
		return PasswordValidators.specialCharacterRule(Montant);
	}

	// public static email(expression?: string): ValidatorFn {
	// 	if (!!expression) {
	// 		return Validators.pattern(expression);
	// 	}
	// 	return Validators.pattern(/^\s*([\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,})(\s*;\s*([\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}))*\s*$/);
	// }

	public static lessThan(max: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (control?.value === null || control?.value === undefined || control?.value === '') {
				return null;
			}

			return parseFloat(control?.value?.toString()?.replace(',', '.')) < max ? null : { lessThan: true, count: max };
		};
	}

	public static lessThanOrEqual(inclusiveMax: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (control?.value === null || control?.value === undefined || control?.value === '') {
				return null;
			}

			return parseFloat(control?.value?.toString()?.replace(',', '.')) <= inclusiveMax ? null : { lessThanOrEqual: true, count: inclusiveMax };
		};
	}

	public static greaterThan(min: number): ValidatorFn {
		return (control: AbstractControl) => {
			return parseFloat(control?.value?.toString()?.replace(',', '.')) > min ? null : { greaterThan: true, count: min };
		};
	}
	public static greaterThanEqual(min: number): ValidatorFn {
		return (control: AbstractControl) => {
			return parseFloat(control?.value?.toString()?.replace(',', '.')) >= min ? null : { min: true, count: min };
		};
	}

	public static greaterThanDepotAndRib(min: number): ValidatorFn {
		return (control: AbstractControl) => {
			return control?.value ? (parseFloat(control?.value?.replace(',', '.')) > min ? null : { min: true, count: min + 1 }) : null;
		};
	}

	public static greaterThanNBCheques(min: number): ValidatorFn {
		return (control: AbstractControl) => {
			return control?.value ? (Number(control?.value) >= min ? null : { minNbCheques: true, count: min }) : null;
		};
	}

	public static greaterThanOrEqual(inclusiveMin: number): ValidatorFn {
		return (control: AbstractControl) => {
			return parseFloat(control?.value?.replace(',', '.')) >= inclusiveMin ? null : { min: true, count: inclusiveMin };
		};
	}

	public static greaterThanOrEqualNullable(inclusiveMin: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			} else {
				return parseFloat(control.value) >= inclusiveMin ? null : { min: true, count: inclusiveMin };
			}
		};
	}

	public static minimumLength(min: number): ValidatorFn {
		return Validators.minLength(min);
	}

	public static maximumLength(max: number): ValidatorFn {
		return Validators.maxLength(max);
	}

	public static exactLength(length: number): ValidatorFn {
		return (control: AbstractControl): { exactLength: ExactLengthError } | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const value = control.value;
			if (value && value.length !== length) {
				return { exactLength: { requiredLength: length } };
			}

			return null;
		};
	}

	public static exactLengths(config: ExactLengthsConfig): ValidatorFn {
		return (control: AbstractControl): { exactLength: ExactLengthsError } | null => {
			const value = control.value;
			if (!value) return null;

			const length = value.length;

			const allowedLengths = [...(config.alpha || []), ...(config.numeric || [])];

			const validLength = allowedLengths.includes(length);
			if (!validLength) {
				const message = CustomValidators.buildMultipleSirenLengthMessage(config);
				return { exactLength: { requiredLength: message } };
			}

			const isNumeric = /^\d+$/.test(value);
			const isAlphanumeric = /^[a-zA-Z0-9]+$/.test(value);

			const validNumeric = config.numeric?.includes(length) && isNumeric;
			const validAlpha = config.alpha?.includes(length) && isAlphanumeric;

			if (validAlpha || validNumeric) {
				return null;
			}

			const message = CustomValidators.buildMultipleSirenLengthMessage(config);
			return { exactLength: { requiredLength: message } };
		};
	}

	public static exactLengthNumber(length: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const value = control.value;

			const hasExactLength = value && value.toString().length === length;

			if (!hasExactLength) {
				return { exactLengthNumber: { requiredLength: length } };
			}

			return null;
		};
	}

	public static regularExpression(expression: string): ValidatorFn {
		return Validators.pattern(new RegExp(expression, 'u'));
	}

	public static validDate(control: AbstractControl): { validDate: true } | null {
		if (CustomValidators.isEmptyInputValue(control.value)) {
			return null;
		}

		const value = control.value as Date;

		if (!isValidDate(value)) {
			return { validDate: true };
		}

		return null;
	}

	public static beforeNow(doNotCheckHours: boolean): ValidatorFn {
		return (control: AbstractControl): { beforeNow: boolean } | null => {
			const value = control.value as Date;

			if (CustomValidators.isEmptyInputValue(value)) {
				return null;
			}

			const dates = getDatesToCompare(value, doNotCheckHours);

			if (dates.dateToTest.isAfter(dates.now)) {
				return { beforeNow: true };
			}

			return null;
		};
	}

	public static afterNow(doNotCheckHours: boolean): ValidatorFn {
		return (control: AbstractControl): { afterNow: boolean } | null => {
			const value = control.value as Date;

			if (CustomValidators.isEmptyInputValue(value)) {
				return null;
			}

			const dates = getDatesToCompare(value, doNotCheckHours);

			if (dates.dateToTest.isBefore(dates.now)) {
				return { afterNow: true };
			}

			return null;
		};
	}

	public static libelleMajuscules(control: AbstractControl): { libelleMajuscules: true } | null {
		if (CustomValidators.isEmptyInputValue(control.value)) {
			return null;
		}
		const value = control.value;
		return value.toLocaleUpperCase() !== value ? { libelleMajuscules: true } : null;
	}

	public static notEqual(valueToCompare: string): ValidatorFn {
		return (control: AbstractControl): { notEqual: { propertyName: string; valueToCompare: string } } | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const value = control.value;
			return value.toLocaleLowerCase() === valueToCompare.toLocaleLowerCase()
				? {
						notEqual: {
							propertyName: Object.keys(control.parent.controls).find(name => control === control.parent.controls[name]),
							valueToCompare: valueToCompare
						}
					}
				: null;
		};
	}

	public static notMultipleMonthlySum(valueToCompare: number, nb: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const value = Number(control.value?.toString().replace(',', '.'));
			const valueForCompare = value * nb;
			return valueForCompare > valueToCompare + 1 || valueForCompare < valueToCompare - 1
				? {
						notEqualMonthlySum: true
					}
				: null;
		};
	}

	public static betweenNumber(from: number, to: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const value = Number(control.value);
			return value > to || value < from ? { notBetweenNumber: { fromVal: from, toVal: to } } : null;
		};
	}

	public static ibanLength(pays: string, longueurIban: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value) || !longueurIban) {
				return null;
			}

			const value = control.value;
			if (value && value?.toString().replace(/\s/g, '')?.replace(AppConstants.REGEX.specialChars, '')?.length !== longueurIban) {
				return { ibanLength: { pays: pays, longueurIban: longueurIban } };
			}

			return null;
		};
	}

	public static respectFormatISO(
		startPosition: number,
		endPosition: number,
		codeISO: string,
		codeISOPaysParent: string = null,
		type: string = 'iban'
	): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const value = control.value;
			let substr = value
				?.toString()
				.replace(/\s/g, '')
				?.replace(AppConstants.REGEX.specialChars, '')
				?.toUpperCase()
				?.substring(startPosition, endPosition + 1);

			if (
				substr != codeISO?.toUpperCase() &&
				(codeISOPaysParent == null || (codeISOPaysParent != null && substr != codeISOPaysParent?.toUpperCase()))
			) {
				return type === 'iban' ? { ibanFormatISO: true } : { swiftFormatISO: true };
			}

			return null;
		};
	}

	public static wrongSwiftBicLength(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const value = control.value;
			if (value.length != 8 && value.length != 11) {
				return { wrongSwiftBicLength: true };
			}
			return null;
		};
	}

	public static wrongSirenLength(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			return { wrongSirenLength: true };
		};
	}

	public static wrongSiretLength(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			return { wrongSiretLength: true };
		};
	}

	public static wrongTvaFormat(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			return { wrongTVAFormat: true };
		};
	}

	public static wrongRegistreEquivalentLength(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			return { wrongRegistreEquivalentLength: true };
		};
	}

	public static wrongRcsFormat(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			return { wrongRcsFormat: true };
		};
	}

	public static wrongRCSFormat(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const pattern = /^(\d{3}\s?\d{3}\s?\d{3})\sRCS\s[A-Z]+(?:[\s-][A-Z]+)*$/;

			if (!pattern.test(control.value)) {
				return { wrongRcsFormat: true };
			} else {
				return null;
			}
		};
	}

	public static wrongTVAFormat(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const pattern = /^FR\d{11}$/;

			if (!pattern.test(control.value.replace(/\s+/g, ''))) {
				return { wrongTVAFormat: true };
			} else {
				return null;
			}
		};
	}

	public static wrongTelephoneLength(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			return { wrongTelephoneLength: true };
		};
	}

	public static wrongIbanKey(): ValidatorFn {
		return (control: AbstractControl) => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const iban = control.value.replace(/\s+/g, '').toUpperCase();
			// Règle métier FR : banque, guichet et compte ≠ 0
			const banque = iban.substr(4, 5);
			const guichet = iban.substr(9, 5);
			const compte = iban.substr(14, 11);
			if (/^0+$/.test(banque) && /^0+$/.test(guichet) && /^0+$/.test(compte)) {
				return { wrongIbanKey: true };
			}

			const parsedValue = (control.value.substr(4) + control.value.substr(0, 4)).toUpperCase().replaceAll(' ', '');
			let valueWithConvertedNumbers: string = '';
			for (let i = 0; i < parsedValue.length; i++) {
				let character: number = parsedValue.charCodeAt(i);

				// If the character is A-Z, we need to
				// convert it to a number from 10 - 35
				if (character > 64 && character < 91) {
					valueWithConvertedNumbers += String(character - 55);
				} else {
					valueWithConvertedNumbers += String.fromCharCode(character);
				}
			}
			let modulo = this.modulo(valueWithConvertedNumbers, 97);
			if (modulo !== 1) {
				return { wrongIbanKey: true };
			}
			return null;
		};
	}

	private static modulo(divident, divisor): number {
		let partLength = 10;
		while (divident.length > partLength) {
			const part = divident.substring(0, partLength);
			divident = (part % divisor) + divident.substring(partLength);
		}
		return divident % divisor;
	}

	public static libelleContraint(expression: string = AppConstants.LIBELLE_CONTRAINT_EXPRESSION): ValidatorFn {
		return (control: AbstractControl): { libelleContraint: boolean } | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const value = control.value;
			return !new RegExp(expression).test(value) ? { libelleContraint: true } : null;
		};
	}

	public static notAllowedTerm(term: string, contains: boolean = false): ValidatorFn {
		return (control: AbstractControl): { notAllowedTerm: boolean } | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}
			const value = control.value.toLocaleLowerCase();
			const match = contains ? value.includes(term.toLocaleLowerCase()) : value === term.toLocaleLowerCase();

			return match ? { notAllowedTerm: true } : null;
		};
	}

	public static olderThanDays(numberOfDays: number): ValidatorFn {
		return (control: AbstractControl): { olderThanDays: DaysComparisonError } | null => {
			const value = control.value as Date;

			if (CustomValidators.isEmptyInputValue(value)) {
				return null;
			}

			const dates = getDatesToCompare(value, true);
			const limit = dates.now.add(numberOfDays, 'day');

			if (dates.dateToTest.isBefore(limit)) {
				return { olderThanDays: { days: numberOfDays } };
			}

			return null;
		};
	}

	public static notOlderThanDays(numberOfDays: number): ValidatorFn {
		return (control: AbstractControl): { notOlderThanDays: DaysComparisonError } | null => {
			const value = control.value as Date;

			if (CustomValidators.isEmptyInputValue(value)) {
				return null;
			}

			const dates = getDatesToCompare(value, true);
			const limit = dates.now.subtract(numberOfDays, 'day');

			if (dates.dateToTest.isBefore(limit)) {
				return { notOlderThanDays: { days: numberOfDays } };
			}

			return null;
		};
	}

	public static youngerThanDays(numberOfDays: number): ValidatorFn {
		return (control: AbstractControl): { youngerThanDays: DaysComparisonError } | null => {
			const value = control.value as Date;

			if (CustomValidators.isEmptyInputValue(value)) {
				return null;
			}

			const dates = getDatesToCompare(value, true);
			const limit = dates.now.add(numberOfDays, 'day');

			if (dates.dateToTest.isAfter(limit)) {
				return { youngerThanDays: { days: numberOfDays } };
			}

			return null;
		};
	}

	public static betweenNumericalValidator(pattern: string): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;

			if (!value) {
				return null;
			}

			if (!(!value[0] || value[0].match(pattern)) || !(!value[1] || value[1].match(pattern))) {
				return { pattern: true };
			}

			if (!value[0] || !value[1]) {
				return { required: true };
			}

			if (Number(value[0]) > Number(value[1])) {
				return { rangeInconsistency: true };
			}

			return null;
		};
	}

	public static formatEmailNotification(): ValidatorFn {
		return (control: AbstractControl) => {
			const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
			const blackListRegex = /[()<>, #£{}'$&=:"\[|ç%]/;

			if (!control?.value) {
				return null;
			}

			let emailValue: string;
			if (typeof control.value === 'string') {
				emailValue = control.value.trim();
			} else {
				emailValue = (control.value[0] ?? '').trim();
			}

			const emails = emailValue
				.split(';')
				.map(e => e.trim())
				.filter(Boolean);

			if (emails.length === 1) {
				const email = emails[0];

				// Contrôles stricts pour un seul email
				if (!emailRegex.test(email)) {
					return { emailInvalid: true };
				}
				if (blackListRegex.test(email)) {
					return { emailInvalid: true };
				}
				if ((email.match(/@/g) || []).length > 1) {
					return { emailInvalid: true };
				}
				if ((email.match(/]/g) || []).length > 0) {
					return { emailInvalid: true };
				}
			} else {
				// Contrôle pour liste d’emails : on valide chaque email uniquement
				for (const email of emails) {
					if (!emailRegex.test(email) || blackListRegex.test(email)) {
						return { emailInvalid: true };
					}
				}
			}

			return null;
		};
	}

	public static emailRequired(): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || control.value === '') {
				return { emailRequired: true };
			}
			return null;
		};
	}

	public static containsCarpaEmail(email: string): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || typeof control.value !== 'string') {
				return null;
			}
			let currentValue = control.value?.toLowerCase();
			if (currentValue.includes(email.toLowerCase())) {
				return { emailInvalid: true };
			}
			return null;
		};
	}

	public static formatPhoneNumber(phone: string): ValidatorFn {
		return (control: AbstractControl) => {
			const regex = new RegExp(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/);
			if (!control.value) {
				return null;
			}
			if (!regex.test(control.value)) {
				return { phoneNumberInvalid: true };
			}
			return null;
		};
	}

	public static documentRequired(): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || control.value === '' || control?.value?.length == 0) {
				return { documentRequired: true };
			}
			return null;
		};
	}

	public static sensOperationRequired(): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || control.value === '' || control?.value?.length == 0) {
				return { sensOperationRequired: true };
			}
			return null;
		};
	}

	public static requiredCommentOrDocument(): ValidatorFn {
		return (group: UntypedFormGroup): ValidationErrors => {
			const commentControl = group.get('commentaire');
			const docsFormGroup = group.get('documents');
			if (
				(!commentControl.value || commentControl?.value === '') &&
				(!docsFormGroup.value || docsFormGroup.value?.filesList?.length === 0)
			) {
				commentControl.setErrors({ requiredCommentOrDocument: true });
			} else {
				commentControl.setErrors(null);
			}
			return;
		};
	}

	// US 9040: mettre le contrôle de la PJ obligatoire sur le type de document
	public static requiredDocumentByType(typeId: string): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.value || !control.value?.find(file => file.typePieceJointeId === typeId)) {
				return { requiredDocumentByType: true };
			}

			return null;
		};
	}

	// Pattern validator
	public static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } => {
			if (!control.value) {
				// if control is empty return no error
				return null;
			}

			// test the value of the control against the regexp supplied
			const valid = regex.test(control.value);

			// if true, return no error (no error), else return error passed in the second parameter
			return valid ? null : error;
		};
	}

	public static amountDeLessOrEqualA(otherAmountField: AbstractControl, isDeAmountFocused: boolean): ValidatorFn {
		return (fieldFocused: AbstractControl) => {
			if (!fieldFocused.value || !otherAmountField.value) {
				// if control is empty return no error
				return null;
			}
			let AValue: number;
			let DeValue: number;

			if (isDeAmountFocused) {
				AValue = checkMontantFormat(otherAmountField.value);
				DeValue = checkMontantFormat(fieldFocused.value);
			} else {
				AValue = checkMontantFormat(fieldFocused.value);
				DeValue = checkMontantFormat(otherAmountField.value);
			}
			if (isNaN(AValue) || isNaN(DeValue)) return null;

			if (isDeAmountFocused && DeValue > AValue) {
				return { amountDeLessOrEqualA: true };
			} else if (isDeAmountFocused == false && DeValue > AValue) {
				return { amountAGreaterThanDe: true };
			}

			return null;
		};
	}

	public static dateRangeValidator(fromDateField: AbstractControl, toDateField: AbstractControl): ValidatorFn {
		return (): ValidationErrors | null => {
			const fromDate = fromDateField.value;
			const toDate = toDateField.value;

			if (fromDate && toDate && fromDate > toDate) {
				return { dateRange: true };
			}

			return null;
		};
	}

	public static dateRangeFromValidator(fromDateField: AbstractControl, toDateField: AbstractControl): ValidatorFn {
		return (): ValidationErrors | null => {
			const fromDate = fromDateField.value;
			const toDate = toDateField.value;

			if (fromDate && toDate && compareDatesOnly(fromDate, toDate) === 1) {
				if (toDateField.hasError('dateRangeToError')) {
					return null;
				} else {
					return { dateRangeFromError: true };
				}
			}

			return null;
		};
	}

	public static dateRangeToValidator(fromDateField: AbstractControl, toDateField: AbstractControl): ValidatorFn {
		return (): ValidationErrors | null => {
			const fromDate = fromDateField.value;
			const toDate = toDateField.value;
			if (fromDate && toDate && compareDatesOnly(fromDate, toDate) === 1) {
				if (fromDateField.hasError('dateRangeFromError')) {
					return null;
				} else {
					return { dateRangeToError: true };
				}
			}

			return null;
		};
	}

	public static invalidDateValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const inputDateString = new Date(control?.value)?.toDateString();
			if (control?.value && inputDateString && inputDateString === 'Invalid Date') {
				return { invalidDateError: true };
			}

			return null;
		};
	}

	public static checkNomEmetteurQualiteAdvocatAdverse(qualiteEmetteurControl: AbstractControl): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const isQualiteAdvocatAdverse = qualiteEmetteurControl?.value?.code === 'AAD';
			const pattern = isQualiteAdvocatAdverse ? /^CARPA [^<>&]*$/ : /^[^<>&]*$/;

			if (!pattern.test(control?.value)) {
				return isQualiteAdvocatAdverse ? { invalidNomEmetteurQualAAD: true } : { invalidNomEmetteurQual: true };
			}
			return null;
		};
	}

	public static allowedImageTypesValidator(allowedTypes: string[]): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const file = control.value as File;
			if (!file || !file.type || allowedTypes.includes(file.type)) {
				return null;
			}

			const allowedExtensions = allowedTypes.map(getFileExtension);
			const formattedExtensions = formatExtensions(allowedExtensions);
			return { invalidFileType: true };
		};
	}

	public static amountGreaterThan(min: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control?.value) return null;

			const current = checkMontantFormat(control.value);
			if (isNaN(current) || isNaN(min)) return null;

			return current >= min ? null : { amountGreaterThan: { min } };
		};
	}

	public static amountLessOrEqualThan(max: number): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control?.value) return null;

			const current = checkMontantFormat(control.value);
			if (isNaN(current) || isNaN(max)) return null;

			return current <= max ? null : { amountLessOrEqualThan: { max } };
		};
	}

	/**
	 * Algorithme de Luhn étendu aux caractères alphanumériques.
	 * Chaque lettre est convertie en chiffre(s) selon : A=10, B=11, ..., Z=35
	 * puis les chiffres résultants sont traités par l'algorithme de Luhn standard.
	 */
	public static luhn(reduceAlphaCharacters: boolean = false): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (CustomValidators.isEmptyInputValue(control.value)) {
				return null;
			}

			const raw: string = control.value.toString().toUpperCase();

			// Convertir chaque caractère en tableau de chiffres
			const digits: number[] = [];
			for (const char of raw) {
				if (/[A-Z]/.test(char)) {
					// A=10 → [1,0], B=11 → [1,1], ..., Z=35 → [3,5]
					const num = char.charCodeAt(0) - 55;
					if (reduceAlphaCharacters) {
						digits.push(this.reduceToOneDigit(num));
					} else {
						digits.push(...num.toString().split('').map(Number));
					}
				} else if (/[0-9]/.test(char)) {
					digits.push(parseInt(char, 10));
				} else {
					// Caractère non autorisé → invalide
					return { luhnInvalid: true };
				}
			}

			// Vérification Luhn : doubler les chiffres de rang pair (depuis la droite)
			if (digits.length > 0 && digits.every(digit => digit === 0)) {
				return { luhnInvalid: true };
			}

			let sum = 0;
			const len = digits.length;
			for (let i = 0; i < len; i++) {
				let d = digits[len - 1 - i];
				if (i % 2 === 1) {
					d *= 2;
					if (d >= 10) {
						d = Math.floor(d / 10) + (d % 10);
					}
				}
				sum += d;
			}

			return sum % 10 === 0 ? null : { luhnInvalid: true };
		};
	}

	private static reduceToOneDigit(value: number): number {
		while (value >= 10) {
			value = Math.floor(value / 10) + (value % 10);
		}

		return value;
	}
}
